import { TokenId, Vault, getTotalTokenIds, getUniqueTokenIds } from 'nftx.js';
import Swap from '../../../widgets/Swap';
import Marketplace from './Marketplace';
import useVaultAssets from '@/application/read/assets/useVaultAssets';
import { useCallback, useEffect, useMemo, useState } from 'react';
import useSwapState from '@/application/read/swap/useSwapState';
import { Address } from 'viem';
import useTraitFilters from '@/presentation/hooks/filters/useTraitFilters';
import { getBestBuyPrice } from '@/application/read/vaults/utils';

export interface MarketplaceContainerProps {
  vaults?: Vault[];
  collectionAddress: Address;
  selectedVault: Vault | undefined;
  onSelectVault: (vault: Vault | undefined) => void;
}

const MarketplaceContainer = ({
  vaults,
  selectedVault,
  onSelectVault,
  collectionAddress,
}: MarketplaceContainerProps) => {
  const [showBasket, setShowBasket] = useState(false);
  const { data: allUnsortedAssets, status: assetsStatus } = useVaultAssets({
    assetAddress: collectionAddress,
  });
  const allAssets = useMemo(() => {
    return allUnsortedAssets?.sort((a, b) => {
      if ((a.dateAdded || 0) > (b.dateAdded || 0)) {
        return -1;
      }
      if ((a.dateAdded || 0) < (b.dateAdded || 0)) {
        return 1;
      }
      return 0;
    });
  }, [allUnsortedAssets]);
  const vaultFilteredAssets = useMemo(() => {
    if (!selectedVault) {
      return allAssets;
    }
    return allAssets?.filter((asset) =>
      asset.vaultIds.includes(selectedVault?.vaultId)
    );
  }, [allAssets, selectedVault]);
  const { filters, toggleTraitFilter, filteredAssets, canFilter, hasFilters } =
    useTraitFilters({
      assets: vaultFilteredAssets,
    });
  const assets = filteredAssets;

  const getVault = useCallback(
    (tokenId: TokenId) => {
      if (!allAssets || !vaults) {
        return;
      }

      // only have 1 vault for this collection
      if (vaults.length === 1) {
        return vaults[0];
      }

      // choose the vault based on selected items
      if (!tokenId) {
        return;
      }

      const selectedAsset = allAssets.find(
        (asset) => tokenId === asset.tokenId
      );
      if (!selectedAsset) {
        return vaults[0];
      }
      const vaultId = selectedAsset.vaultIds[0];
      const vault = vaults.find((vault) => vault.vaultId === vaultId);
      return vault;
    },
    [allAssets, vaults]
  );

  const [state, dispatch] = useSwapState({
    buyType: 'NFT',
    sellType: 'ETH',
  });

  const onToggleTokenId = (tokenId: TokenId) => {
    const newTokenIds = [...state.buyItems];
    const item = newTokenIds.find(([x]) => x === tokenId);

    if (item) {
      const i = newTokenIds.indexOf(item);
      newTokenIds.splice(i, 1);
    } else {
      newTokenIds.push([tokenId, 1]);
    }

    const vault = getVault(tokenId);

    dispatch({
      type: 'buy_nfts',
      assetAddress: collectionAddress,
      items: newTokenIds,
      vaultId: vault?.vaultId ?? '',
      vaultAddress: vault?.id ?? '0x',
    });
  };

  const onQuantityChange = (tokenId: TokenId, quantity: number) => {
    const asset = assets?.find((asset) => asset.tokenId === tokenId);
    const maxQuantity = Number(asset?.quantity || 0n);

    quantity = Math.max(0, Math.min(maxQuantity, quantity));

    const newTokenIds = [...state.buyItems];
    const item = newTokenIds.find(([x]) => x === tokenId);

    if (item && quantity > 0) {
      const i = newTokenIds.indexOf(item);
      newTokenIds.splice(i, 1, [tokenId, quantity]);
    } else if (item && quantity === 0) {
      const i = newTokenIds.indexOf(item);
      newTokenIds.splice(i, 1);
    } else if (!item && quantity > 0) {
      newTokenIds.push([tokenId, quantity]);
    }

    const vault = getVault(tokenId);

    dispatch({
      type: 'buy_nfts',
      assetAddress: collectionAddress,
      items: newTokenIds,
      vaultId: vault?.vaultId ?? '',
      vaultAddress: vault?.id ?? '0x',
    });
  };

  const vault = useMemo(() => {
    const tokenIds = getUniqueTokenIds(state.buyItems);

    for (const tokenId of tokenIds) {
      const vault = getVault(tokenId);
      if (vault) {
        return vault;
      }
    }
  }, [getVault, state.buyItems]);

  useEffect(() => {}, []);

  const handleComplete = () => {
    dispatch({ type: 'reset' });
  };

  const bestBuyPrice = useMemo(() => {
    return getBestBuyPrice(vaults);
  }, [vaults]);

  const noLiquidity = bestBuyPrice === 0n;

  return (
    <Marketplace
      swap={
        <Swap
          {...state}
          title={`Basket ${
            state.buyItems.length ? `(${getTotalTokenIds(state.buyItems)})` : ''
          }`}
          dispatch={dispatch}
          buyType="NFT"
          buyToken={vault?.asset?.id ?? ''}
          buyVaultId={vault?.vaultId ?? ''}
          buyVaultAddress={vault?.id ?? ''}
          sellVaultId={vault?.vaultId ?? ''}
          sellVaultAddress={vault?.id ?? ''}
          sellToken={vault?.id ?? ''}
          canChangeBuyToken={false}
          canChangeSellToken={true}
          canFlip={false}
          showBuy={state.buyItems.length > 0}
          className="rounded-t-xl"
          onComplete={handleComplete}
          filterController="buy"
          amountController="buy"
        />
      }
      vault={vault}
      vaults={vaults}
      assets={assets}
      assetsStatus={assetsStatus}
      tokenIds={state.buyItems}
      onToggleTokenId={onToggleTokenId}
      onSelectVault={onSelectVault}
      selectedVault={selectedVault}
      setShowBasket={setShowBasket}
      showBasket={showBasket}
      filters={filters}
      onFilter={toggleTraitFilter}
      canFilter={canFilter}
      hasFilters={hasFilters}
      noLiquidity={noLiquidity}
      onQuantityChange={onQuantityChange}
    />
  );
};

export default MarketplaceContainer;
